import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import { fmtCHF2 } from '../utils/format';
import { LigneFlux, ScenarioBudget, TagRevenu, MOIS_LABELS } from '../types';
import { BadgeTagRevenu, ChoixTagScenario } from './TagScenarioRevenu';
import { IMPACT_VIDE, fusionnerImpacts, impactPortee, listerMois } from '../utils/portee';

type Portee = 'sansFin' | 'ceMois' | 'jusqua';

interface Props {
  ligne: LigneFlux; // état de la grille AVANT édition (sert aux valeurs de retour)
  mois: number; // 1..12
  annee: number;
  scenarios: ScenarioBudget[];
  scenarioCourant: number | null; // scénario affiché dans la grille (null = décisions seules)
  ancre: { top: number; bottom: number; left: number; right: number };
  onFermer: () => void;
  onModifie: () => void;
  onOuvrirFiche: () => void;
}

const LARGEUR = 380;

function iso(annee: number, mois: number): string {
  return `${annee}-${String(mois).padStart(2, '0')}`;
}

/**
 * Éditeur rapide des revenus : popover ancré à la cellule (double-clic).
 * Champs selon le mode du flux — montant direct, ou quantité (OAI ; le tarif
 * fait foi dans la convention cadre et se modifie dans le module Contrats).
 * Portée : dès ce mois sans fin / ce mois seulement / de ce mois à un mois
 * donné (un événement de retour ré-affirme la valeur précédente après la
 * plage, sauf si un changement existant prend déjà effet à ce moment-là).
 * Les projections ultérieures couvertes par la portée sont remplacées par la
 * valeur saisie (annoncé avant l'enregistrement) — sans quoi elles
 * reprendraient la main dès leur mois d'effet et « sans fin » ne tiendrait
 * pas sa promesse ; les définitifs, eux, ne sont jamais écrasés.
 * Les flux à recettes libres et les flux dérivés d'une convention s'éditent
 * dans la fiche / les modules dédiés, pas ici.
 */
export function EditionRapideRevenu({ ligne, mois, annee, scenarios, scenarioCourant, ancre, onFermer, onModifie, onOuvrirFiche }: Props) {
  const cellule = ligne.mois[mois - 1];
  const [montant, setMontant] = useState(String(Math.round(cellule.montant * 100) / 100));
  const [quantite, setQuantite] = useState(String(cellule.quantite ?? 0));
  const tarifEnVigueur = cellule.tarif ?? 0;
  const [portee, setPortee] = useState<Portee>('sansFin');
  const [finMois, setFinMois] = useState(Math.min(mois + 1, 12));
  const [tag, setTag] = useState<'definitif' | 'projete'>(scenarioCourant != null ? 'projete' : 'definitif');
  const [scenarioId, setScenarioId] = useState<number | null>(scenarioCourant);
  const [commentaireManuel, setCommentaireManuel] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  // La valeur affichée est figée : il faut cliquer « Corriger » pour saisir.
  const [deverrouille, setDeverrouille] = useState(false);
  const premierChamp = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (deverrouille) {
      premierChamp.current?.focus();
      premierChamp.current?.select();
    }
  }, [deverrouille]);

  const nomScenario = (id: number | null) =>
    id == null ? null : scenarios.find((s) => s.id === id)?.nom ?? `#${id}`;

  // Un mois futur dont la valeur n'est qu'un report (mois_effet antérieur au
  // mois affiché) n'est pas un définitif : c'est une prévision.
  const maintenant = new Date();
  const futur = annee > maintenant.getFullYear() ||
    (annee === maintenant.getFullYear() && mois > maintenant.getMonth() + 1);
  const badgeValeur = (tag: TagRevenu | null, moisEffet: string | null) => {
    if (!tag) return null;
    if (tag !== 'projete' && futur && moisEffet != null && moisEffet.slice(0, 7) < iso(annee, mois)) {
      const [a, m] = moisEffet.split('-');
      return (
        <span
          className="badge badge-warn badge-sm"
          title={`Prévision : report du dernier ${tag === 'consolide' ? 'consolidé' : 'définitif'} en vigueur (${MOIS_LABELS[Number(m) - 1].toLowerCase()} ${a}). Aucune décision ne prend effet ce mois-ci.`}
        >
          prévision
        </span>
      );
    }
    return <BadgeTagRevenu tag={tag} scenarioNom={nomScenario(scenarioCourant)} />;
  };

  const estQT = ligne.mode === 'quantite_tarif';
  // Tarif au temps passé (coaching OAI) : le nombre d'assurés ne dérive aucun
  // montant — les deux se saisissent, le montant fait foi, la quantité reste
  // l'indicateur de pilotage.
  const estHoraire = estQT && cellule.tarifHoraire;
  const montantNum = Number(montant);
  const quantiteNum = Number(quantite);
  const montantChange = (!estQT || estHoraire) && Math.abs(montantNum - cellule.montant) > 0.004;
  const quantiteChange = estQT && Math.abs(quantiteNum - (cellule.quantite ?? 0)) > 0.004;

  const porteeTexte = portee === 'sansFin'
    ? `dès ${MOIS_LABELS[mois - 1].toLowerCase()} ${annee}, sans fin`
    : portee === 'ceMois'
      ? `${MOIS_LABELS[mois - 1].toLowerCase()} ${annee} seulement`
      : `de ${MOIS_LABELS[mois - 1].toLowerCase()} à ${MOIS_LABELS[finMois - 1].toLowerCase()} ${annee}`;

  // Projections ultérieures dans la portée : elles masqueraient l'édition dès
  // leur mois d'effet — la valeur saisie les remplace (les définitifs restent).
  const moisFinPortee = portee === 'sansFin' ? 12 : portee === 'jusqua' ? finMois : mois;
  const impactMontant = montantChange
    ? impactPortee(ligne.mois.map((c) => c.changementMontant?.tag), mois, moisFinPortee)
    : IMPACT_VIDE;
  const impactQuantite = quantiteChange
    ? impactPortee(ligne.mois.map((c) => c.changementQuantite?.tag), mois, moisFinPortee)
    : IMPACT_VIDE;
  const impact = fusionnerImpacts(impactMontant, impactQuantite);

  // Sur un flux quantité × tarif, un montant budgété (projeté au mois exact,
  // p.ex. la moyenne 3 mois de Base) remplace quantité × tarif dans la grille :
  // changer la quantité seule serait sans effet sur le revenu. Ces montants
  // budgétés sont donc réalignés sur la nouvelle quantité × tarif — jamais les
  // montants facturés réels (définitifs).
  const moisMontantAligne = estQT && !estHoraire && quantiteChange
    ? [mois, ...impactQuantite.aRemplacer].filter(
        (m2) => ligne.mois[m2 - 1].changementMontant?.tag === 'projete')
    : [];

  const commentaireAuto = useMemo(() => {
    const parts: string[] = [];
    if (montantChange) parts.push(`montant ${fmtCHF2(cellule.montant)} → ${fmtCHF2(montantNum)}`);
    if (quantiteChange) parts.push(`quantité ${cellule.quantite ?? 0} → ${quantiteNum}`);
    return `Édition rapide : ${parts.length ? parts.join(', ') : 'sans changement'}, ${porteeTexte}`;
  }, [montantChange, quantiteChange, cellule, montantNum, quantiteNum, porteeTexte]);

  const commentaire = commentaireManuel ?? commentaireAuto;

  const valide =
    (montantChange || quantiteChange) &&
    montantNum >= 0 && quantiteNum >= 0 &&
    commentaire.trim().length > 0 &&
    (tag === 'definitif' || scenarioId != null) &&
    (portee !== 'jusqua' || finMois >= mois);

  async function creerEvenements(champ: 'montant' | 'quantite', valeur: number) {
    const chemin = `${champ}-evenements`;
    const base = tag === 'projete' ? { tag, scenarioId } : { tag };
    await api.post(`/revenus/flux/${ligne.id}/${chemin}`, {
      moisEffet: iso(annee, mois), [champ]: valeur, ...base, commentaire: commentaire.trim(),
    });
    // Les projections ultérieures de la portée reprendraient la main dès leur
    // mois d'effet : on les remplace par la même valeur, mois par mois.
    const aRemplacer = (champ === 'montant' ? impactMontant : impactQuantite).aRemplacer;
    for (const m2 of aRemplacer) {
      await api.post(`/revenus/flux/${ligne.id}/${chemin}`, {
        moisEffet: iso(annee, m2), [champ]: valeur, ...base,
        commentaire: `${commentaire.trim()} — remplace la projection de ${MOIS_LABELS[m2 - 1].toLowerCase()}`,
      });
    }
    if (champ === 'quantite') {
      for (const m2 of moisMontantAligne) {
        const tarifMois = ligne.mois[m2 - 1].tarif ?? 0;
        await api.post(`/revenus/flux/${ligne.id}/montant-evenements`, {
          moisEffet: iso(annee, m2),
          montant: Math.round(valeur * tarifMois * 100) / 100, ...base,
          commentaire: `${commentaire.trim()} — montant budgété réaligné : ${valeur} × CHF ${fmtCHF2(tarifMois)}`,
        });
      }
    }
    if (portee === 'sansFin') return;

    const dernierMois = portee === 'ceMois' ? mois : finMois;
    const retourMois = dernierMois + 1;
    let moisEffetRetour: string;
    let valeurRetour: number;
    let dejaUnChangement = false;
    if (retourMois <= 12) {
      const cell = ligne.mois[retourMois - 1];
      valeurRetour = champ === 'montant' ? cell.montant : champ === 'quantite' ? (cell.quantite ?? 0) : (cell.tarif ?? 0);
      dejaUnChangement = !!(champ === 'montant' ? cell.changementMontant
        : champ === 'quantite' ? cell.changementQuantite : cell.changementTarif);
      moisEffetRetour = iso(annee, retourMois);
    } else {
      // La plage couvre décembre : le retour se pose en janvier de l'année suivante
      const cell = ligne.mois[11];
      valeurRetour = champ === 'montant' ? cell.montant : champ === 'quantite' ? (cell.quantite ?? 0) : (cell.tarif ?? 0);
      moisEffetRetour = iso(annee + 1, 1);
    }
    if (!dejaUnChangement) {
      await api.post(`/revenus/flux/${ligne.id}/${chemin}`, {
        moisEffet: moisEffetRetour, [champ]: valeurRetour, ...base,
        commentaire: `Fin d'application (${porteeTexte}) — retour à la valeur précédente`,
      });
    }
  }

  async function enregistrer() {
    if (!valide || enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      if (montantChange) await creerEvenements('montant', montantNum);
      if (quantiteChange) await creerEvenements('quantite', quantiteNum);
      onModifie();
      onFermer();
    } catch (e) {
      setErreur((e as Error).message);
      setEnCours(false);
    }
  }

  // Position : sous la cellule, au-dessus si la place manque. La hauteur est
  // **mesurée après rendu** — le popover double de taille au clic sur
  // « Corriger » (champs, portée, commentaire, avertissements d'impact) et une
  // hauteur estimée laissait les boutons et les avertissements sous le pli.
  const boite = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(ancre.bottom + 6);
  useLayoutEffect(() => {
    const el = boite.current;
    if (!el) return;
    const h = el.offsetHeight;
    const vise = ancre.bottom + 6 + h < window.innerHeight
      ? ancre.bottom + 6
      : Math.max(8, Math.min(ancre.top - h - 6, window.innerHeight - h - 8));
    setTop((prec) => (Math.abs(prec - vise) < 1 ? prec : vise));
  });
  const left = Math.min(Math.max(8, ancre.left - 80), window.innerWidth - LARGEUR - 12);

  const apercu = estQT && !estHoraire ? quantiteNum * tarifEnVigueur : montantNum;

  return (
    <>
      <div className="edition-rapide__backdrop" onClick={onFermer} />
      <div
        ref={boite}
        className="edition-rapide"
        style={{ top, left, width: LARGEUR }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onFermer();
          if (e.key === 'Enter' && deverrouille && (e.target as HTMLElement).tagName !== 'SELECT') enregistrer();
        }}
      >
        <div className="flex items-center justify-between mb-8">
          <div className="edition-rapide__titre" style={{ marginBottom: 0 }}>
            {ligne.libelle} — {MOIS_LABELS[mois - 1]} {annee}
          </div>
          <button
            type="button"
            className="edition-rapide__fiche"
            onClick={onOuvrirFiche}
            title="Ouvrir la fiche complète (historique, convention, paramètres)"
          >
            Fiche complète ↗
          </button>
        </div>

        {!deverrouille ? (
          <>
            <div className="flex flex-col gap-8 mb-8">
              {estQT ? (
                <>
                  <div className="flex items-center gap-8 text-sm">
                    <span>Assurés : <strong>{cellule.quantite ?? 0}</strong></span>
                    {badgeValeur(cellule.tagQuantite, cellule.moisEffetQuantite)}
                    <span className="text-ink-3">· tarif CHF {fmtCHF2(tarifEnVigueur)}{estHoraire ? ' /heure' : ''}</span>
                  </div>
                  <div className="flex items-center gap-8 text-sm">
                    <span>Montant : <strong>CHF {fmtCHF2(cellule.montant)}</strong>{cellule.montantReel ? ' (facturé réel)' : ''}</span>
                    {(cellule.montantReel || estHoraire) && badgeValeur(cellule.tagMontant, cellule.moisEffetMontant)}
                  </div>
                  {estHoraire && (
                    <div className="text-xs text-ink-3">
                      Facturé au temps passé : le montant ne se dérive pas du nombre d’assurés.
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-8 text-sm">
                  <span>Montant : <strong>CHF {fmtCHF2(cellule.montant)}</strong></span>
                  {badgeValeur(cellule.tagMontant, cellule.moisEffetMontant)}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-ink-3">Valeur figée — corriger crée un nouvel événement tracé.</span>
              <div className="flex gap-8">
                <button className="btn btn-sm" onClick={onFermer}>Fermer</button>
                <button className="btn btn-accent btn-sm" onClick={() => setDeverrouille(true)}>Corriger ✎</button>
              </div>
            </div>
          </>
        ) : (
        <>
        <div className="flex gap-12 items-end mb-8 flex-wrap">
          {estQT ? (
            <>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Quantité / mois</label>
                <input
                  ref={premierChamp}
                  type="number"
                  className="form-input"
                  style={{ width: 90 }}
                  min={0} step={1}
                  value={quantite}
                  onChange={(e) => setQuantite(e.target.value)}
                  onFocus={(e) => e.target.select()}
                />
              </div>
              {estHoraire && (
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Montant (CHF / mois)</label>
                  <input
                    type="number"
                    className="form-input"
                    style={{ width: 130 }}
                    min={0} step={0.05}
                    value={montant}
                    onChange={(e) => setMontant(e.target.value)}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
              )}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Tarif en vigueur</label>
                <div className="text-sm" style={{ paddingTop: 6 }}
                  title={estHoraire
                    ? 'Tarif horaire de la convention cadre OAI (facturé au quart d’heure entamé) — modifier dans le module Contrats'
                    : 'Le tarif fait foi dans la convention cadre OAI — modifier dans le module Contrats'}>
                  <strong>CHF {fmtCHF2(tarifEnVigueur)}{estHoraire ? ' /h' : ''}</strong>{' '}
                  <a href="#contrats" className="text-accent" onClick={onFermer}>Contrats ↗</a>
                </div>
              </div>
            </>
          ) : (
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">
                Montant (CHF{ligne.moisFacturation ? ' / versement' : ' / mois'})
              </label>
              <input
                ref={premierChamp}
                type="number"
                className="form-input"
                style={{ width: 130 }}
                min={0} step={0.05}
                value={montant}
                onChange={(e) => setMontant(e.target.value)}
                onFocus={(e) => e.target.select()}
              />
            </div>
          )}
        </div>

        <div className="mb-8">
          <ChoixTagScenario tag={tag} scenarioId={scenarioId} scenarios={scenarios}
            onChange={(t, s) => { setTag(t); setScenarioId(s); }} />
        </div>

        <div className="edition-rapide__portee mb-8">
          <label className="edition-rapide__radio">
            <input type="radio" name="portee-revenu" checked={portee === 'sansFin'} onChange={() => setPortee('sansFin')} />
            <span>dès {MOIS_LABELS[mois - 1].toLowerCase()}, sans fin</span>
          </label>
          <label className="edition-rapide__radio">
            <input type="radio" name="portee-revenu" checked={portee === 'ceMois'} onChange={() => setPortee('ceMois')} />
            <span>{MOIS_LABELS[mois - 1].toLowerCase()} seulement</span>
          </label>
          <label className="edition-rapide__radio">
            <input type="radio" name="portee-revenu" checked={portee === 'jusqua'} onChange={() => setPortee('jusqua')} />
            <span>
              de {MOIS_LABELS[mois - 1].toLowerCase()} à{' '}
              <select
                className="sort-select"
                style={{ minWidth: 70 }}
                value={finMois}
                onChange={(e) => { setFinMois(Number(e.target.value)); setPortee('jusqua'); }}
              >
                {MOIS_LABELS.map((l, i) => i + 1 >= mois && <option key={i} value={i + 1}>{l}</option>)}
              </select>
            </span>
          </label>
        </div>

        <input
          className="form-input w-full mb-8"
          style={{ fontSize: 'var(--t-11)' }}
          title="Commentaire (pré-rempli, modifiable)"
          value={commentaire}
          onChange={(e) => setCommentaireManuel(e.target.value)}
        />

        {/* Les projections retenues (celles du budget) sont invisibles dans la
            lecture du tronc : l'impact de portée ci-dessous ne peut pas les
            annoncer, et elles reprendront la main dès leur mois d'effet. */}
        {scenarioCourant == null && moisFinPortee > mois && (
          <div className="edition-rapide__impact mb-8">
            <div className="edition-rapide__impact-warn">
              ⚠ Lecture « décidé seulement » : les hypothèses du budget n'y figurent pas et ne seront
              donc pas remplacées. Au-delà de {MOIS_LABELS[mois - 1].toLowerCase()}, la lecture
              « Budget » peut continuer d'afficher ses propres valeurs — passer en lecture « Budget »
              pour corriger le budget lui-même.
            </div>
          </div>
        )}

        {(impact.aRemplacer.length > 0 || impact.proteges.length > 0 || moisMontantAligne.length > 0) && (
          <div className="edition-rapide__impact mb-8">
            {impact.aRemplacer.length > 0 && (
              <div className="edition-rapide__impact-warn">
                ⚠ Remplace aussi {impact.aRemplacer.length > 1 ? 'les projections' : 'la projection'} de {listerMois(impact.aRemplacer)}.
              </div>
            )}
            {moisMontantAligne.length > 0 && (
              <div className="edition-rapide__impact-warn">
                {moisMontantAligne.length > 1
                  ? `Les montants budgétés de ${listerMois(moisMontantAligne)} sont réalignés`
                  : `Le montant budgété de ${listerMois(moisMontantAligne)} est réaligné`} sur quantité × tarif.
              </div>
            )}
            {impact.proteges.length > 0 && (
              <div className="text-ink-3">
                {impact.proteges.length > 1 ? 'Les définitifs' : 'Le définitif'} de {listerMois(impact.proteges)} {impact.proteges.length > 1 ? 'restent' : 'reste'} en vigueur.
              </div>
            )}
          </div>
        )}

        {erreur && <div className="text-err text-sm mb-8">{erreur}</div>}

        <div className="flex items-center justify-between">
          <span className="text-xs text-ink-3">
            {montantChange || quantiteChange
              ? <>Mensuel : <strong>CHF {fmtCHF2(apercu)}</strong></>
              : 'Modifier une valeur…'}
          </span>
          <div className="flex gap-8">
            <button className="btn btn-sm" onClick={onFermer}>Annuler</button>
            <button className="btn btn-accent btn-sm" disabled={!valide || enCours} onClick={enregistrer}>
              {enCours ? 'Enregistrement…' : 'OK ⏎'}
            </button>
          </div>
        </div>
        </>
        )}
      </div>
    </>
  );
}
